import { FC } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

import Dashboard from './pages/dashboard';
import Login from './pages/auth/Login';
import InitPassword from './pages/auth/SetNewPassword';
import EmployeesOverview from './pages/employees';
import { EmployeeProfilDashboard } from './pages/profile/dashboard';
import { EmployeeProfilPersonalData } from './pages/profile/data/personal';
import { EmployeeProfilAddressData } from './pages/profile/data/address';
import { EmployeeProfilContactData } from './pages/profile/data/contact';
import { EmployeeProfilPositionData } from './pages/profile/data/position';

import { OauthLogin } from './pages/auth/OauthLogin';
import { observer } from 'mobx-react-lite';
import { useStore } from './hooks/useStore';
import { PXTRA_CARD_FEATURE_FLAG } from './hooks/usePxtraCardEnabled';
import { EmployeeProfilTaxData } from './pages/profile/data/tax';
import { EmployeeProfilePictureUpload } from './pages/profile/data/picture';
import { BasePage } from './pages/partials/basePage';
import { ProfilPagesPartial } from './pages/profile/partials/profilePagesPartial';
import { NotFoundPage } from './pages/NotFound';
import { Footer } from './pages/auth/partials/Footer';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { EmployeeBenefitsPage } from './pages/profile/benefits/basePage';
import { EmployeeBenefitBudget } from './pages/profile/benefits/budget';
import { EmployeeBenefitsOverview } from './pages/profile/benefits';
import { EmployeeReceiptsOverview } from './pages/profile/receipts';
import { ActionStepperPartial } from './pages/actions/partials/actionStepperPartial';
import { CreateNewEmployee } from './pages/actions/createNewEmployee';
import { UpdateBenefitBudget } from './pages/actions/updateBudget';
import { UpdateEmployees } from './pages/actions/updateEmployees';
import { ReiceiptDetailView } from './pages/receipts/detailView';
import { ReceiptsOverview } from './pages/receipts';
import { ReceiptsPagesPartial } from './pages/receipts/partials/receiptsPagesPartial';
import { OnboardingScreen } from './pages/actions/onboarding';
import { ExportsPagesPartial } from './pages/tasks/partials/exportsPagesPartial';
import { BenefitCalculator } from './pages/calculator';
import Company from './pages/company';
import { CompanyCoreData } from './pages/company/settings/data';
import { CompanyContactData } from './pages/company/settings/contact';
import { CompanyLocationsData } from './pages/company/settings/locations';
import { LocationData } from './pages/company/settings/locations/data';
import { CompanyDepartmentsData } from './pages/company/settings/departments';
import { DepartmentData } from './pages/company/settings/departments/data';
import { CompanyCostCentersData } from './pages/company/settings/costCenters';
import { CostCenterData } from './pages/company/settings/costCenters/data';
import { CompanyPaymentData } from './pages/company/settings/payment/general';

import { CompanyGeneralSecurity } from './pages/company/settings/security/general';
import { CompanyPaymentAddressData } from './pages/company/settings/payment/adress';
import { ExportsOverview } from './pages/tasks/overview';
import { ExportsPage } from './pages/tasks/exports';
import { ImportsPage } from './pages/tasks/imports';
import { CreateCustomBenefit } from './pages/actions/createCustomBenefit';
import { CreateCustomBenefitContextProvider } from './pages/actions/createCustomBenefit/hooks/useCreateCustomBenefit';
import { TaskDetailsPage } from './pages/tasks/details';
import { CompanyPayrollSettings } from './pages/company/settings/payroll';
import { UserChangePassword } from './pages/profile/settings/password';
import { WelcomeEmployee } from './pages/auth/Welcome';
import { CreateImportContextProvider } from './pages/actions/createImport/hooks/useCreateImport';
import { CreateImport } from './pages/actions/createImport';
import { HrisPause } from './pages/company/settings/integrations/hrisPause';
import { GroupData } from './pages/company/settings/groups';
import { EmployeeProfilGroupData } from './pages/profile/data/groups';
import { ManageTwoFactorAuth } from './pages/profile/settings/twofactor';
import {
  ManageAppSingleSignOn,
  ManagePlatformSingleSignOn
} from './pages/profile/settings/singlesignon';
import Benefits from './pages/benefits';
import { Bonus } from './pages/bonus';
import { Portfolio } from './pages/benefits/portfolio';
import { CustomBenefits } from './pages/benefits/custom';
import { GroupContextProvider } from './pages/company/settings/groups/context';
import { AcceptAgreements } from './pages/auth/AcceptAgreements';
import { EmployeeSelection } from './pages/actions/createCustomBenefit/steps/employee';
import { ExportFlow } from './pages/exports/components/flow';
import { ExportStepWrapper } from './pages/exports/components/step-wrapper';
import { CompanyLogo } from './pages/company/settings/logo';
import { CreateBenefitSet } from './pages/actions/createBenefitSet';
import { CreateBenefitSetContextProvider } from './pages/actions/createBenefitSet/hooks/useCreateBenefitSet';
import { BenefitSets } from './pages/benefits/benefitSet';
import { CardApprovals } from './pages/benefits/pxtraCard';
import { CardOverview } from './pages/benefits/pxtraCardOverview';
import { CardConsentReturn } from './pages/benefits/pxtraCardConsentReturn';
import { CardSettings } from './pages/benefits/pxtraCardSettings';
import { CardSetupReturn } from './pages/benefits/pxtraCardSetupReturn';
import { CardAreaIndexRedirect } from './pages/benefits/pxtraCardIndexRedirect';
import { CardWallet } from './pages/benefits/pxtraCardWallet';
import { CreateBonus } from './pages/actions/createBonus';
import { CreateBonusContextProvider } from './pages/actions/createBonus/hooks/useCreateBonus';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { CompanyDefaultValues } from './pages/company/settings/standard-values';
import { KundenWerbenKunden } from './pages/company/kundenWerbenKunden';

/**
 * Routes wrapped with this component are private and
 * may also be available under a certain condition only
 * */
const ProtectedRoute: FC<{ children: JSX.Element; condition?: boolean; redirectTo?: string }> =
  observer(({ children, condition = true, redirectTo = '/dashboard' }) => {
    const { authenticationStore } = useStore();
    const location = useLocation();

    if (!authenticationStore.isLoggedIn) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    } else {
      const userId = authenticationStore.currentUserId?.id;
      if (userId) {
        window.jimo?.push(['do', 'identify', [userId]]);
      }
    }

    if (!condition) {
      return <Navigate to={redirectTo} replace />;
    }

    return children;
  });

/**
 * Routes wrapped with this component require a specific feature flag to be enabled
 * */
const FeatureProtectedRoute: FC<{ children: JSX.Element; feature: string; redirectTo?: string }> =
  observer(({ children, feature, redirectTo = '/dashboard' }) => {
    const { isEnabled, isLoading } = useFeatureFlag(feature);

    if (isLoading) {
      return null;
    }

    if (!isEnabled) {
      return <Navigate to={redirectTo} replace />;
    }

    return children;
  });

const AppRouter = () => {
  const { companyStore } = useStore();
  const isBonusStandalone = companyStore.company?.is_bonus_standalone ?? false;
  const defaultRoute = isBonusStandalone ? '/bonus' : '/dashboard';

  return (
    <>
      <AcceptAgreements />
      <Routes>
        {/* 
        Default Homepage
        if the user is not logged in, they will be redirected to login page
      */}
        <Route index element={<Navigate to={defaultRoute} />} />

        {/* Partial: Set Appbar with no Links for nested Routes */}
        <Route element={<BasePage hasAppbar />}>
          <Route
            path="actions"
            element={
              <CreateImportContextProvider>
                <ActionStepperPartial />
              </CreateImportContextProvider>
            }
          >
            <Route path="create-employee">
              <Route index element={<Navigate to="1" />} />
              <Route
                path=":step"
                element={
                  <ProtectedRoute>
                    <CreateNewEmployee />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="update-budget">
              <Route index element={<Navigate to="1" />} />
              <Route
                path=":step"
                element={
                  <ProtectedRoute>
                    <UpdateBenefitBudget />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="update-employees">
              <Route index element={<Navigate to="1" />} />
              <Route
                path=":step"
                element={
                  <ProtectedRoute>
                    <UpdateEmployees />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="onboarding">
              <Route index element={<Navigate to="1" />} />
              <Route
                path=":step"
                element={
                  <ProtectedRoute>
                    <OnboardingScreen />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="custom-benefit">
              <Route index element={<Navigate to="1" />} />
              <Route
                path=":type/:step"
                element={
                  <ProtectedRoute>
                    <CreateCustomBenefitContextProvider>
                      <CreateCustomBenefit />
                    </CreateCustomBenefitContextProvider>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="create-import">
              <Route index element={<Navigate to="employees/1" />} />
              <Route
                path=":type/:step"
                element={
                  <ProtectedRoute>
                    <CreateImport />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="benefit-set">
              <Route index element={<Navigate to="1" />} />
              <Route
                path=":type/:step"
                element={
                  <ProtectedRoute>
                    <CreateBenefitSetContextProvider>
                      <CreateBenefitSet />
                    </CreateBenefitSetContextProvider>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="bonus">
              <Route index element={<Navigate to="1" />} />
              <Route
                path=":type/:step/:id?"
                element={
                  <ProtectedRoute>
                    <CreateBonusContextProvider>
                      <CreateBonus />
                    </CreateBonusContextProvider>
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>
        </Route>

        {/* Partial: Set background color for all nested routes */}
        <Route element={<BasePage backgroundColor="grey.100" />}>
          {/* 
          Login Routes
          if the user is already logged in, they will be forwarded to dashboard
        */}
          <Route element={<Footer />}>
            <Route path="login" element={<Login />} />
            <Route path="login/sso/google" element={<OauthLogin provider="google" />} />
            <Route path="login/sso/microsoft" element={<OauthLogin provider="microsoft" />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="set-new-password" element={<InitPassword forEmployee={true} />} />
            <Route path="set-new-user-password" element={<InitPassword forEmployee={false} />} />
            <Route path="start/:lang" element={<WelcomeEmployee />} />
          </Route>
        </Route>

        {/* Partial: Set Appbar for all nested pages */}
        <Route element={<BasePage hasAppbar={true} />}>
          <Route
            path="dashboard"
            element={
              <ProtectedRoute condition={!isBonusStandalone} redirectTo="/bonus">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="benefits"
            element={
              <ProtectedRoute condition={!isBonusStandalone} redirectTo="/bonus">
                <Benefits />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate replace to="portfolio" />} />
            <Route path="portfolio">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <Portfolio />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="benefit-set">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <BenefitSets />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="pxtra-card"
              element={
                <FeatureProtectedRoute feature={PXTRA_CARD_FEATURE_FLAG} redirectTo="/benefits">
                  <Outlet />
                </FeatureProtectedRoute>
              }
            >
              <Route index element={<CardAreaIndexRedirect />} />
              <Route
                path="wallet"
                element={
                  <ProtectedRoute>
                    <CardWallet />
                  </ProtectedRoute>
                }
              />
              <Route
                path="cards"
                element={
                  <ProtectedRoute>
                    <CardOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="approvals"
                element={
                  <ProtectedRoute>
                    <CardApprovals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="consent-return"
                element={
                  <ProtectedRoute>
                    <CardConsentReturn />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute>
                    <CardSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="setup-return"
                element={
                  <ProtectedRoute>
                    <CardSetupReturn />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="custom">
              <Route
                index
                element={
                  <ProtectedRoute>
                    <CustomBenefits />
                  </ProtectedRoute>
                }
              />
              <Route
                path="edit_custom_benefit_assignment"
                element={
                  <ProtectedRoute>
                    <EmployeeSelection />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="default-values"
              element={
                <ProtectedRoute>
                  <CompanyDefaultValues />
                </ProtectedRoute>
              }
            />
            <Route path="settings">
              <Route
                path="payroll"
                element={
                  <ProtectedRoute>
                    <CompanyPayrollSettings />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>
          <Route path="employees">
            <Route
              index
              element={
                <ProtectedRoute>
                  <EmployeesOverview />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="employees" element={<ProfilPagesPartial />}>
            {['receipts/:receiptId', 'receipts/:receiptId/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <ReiceiptDetailView />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
          </Route>

          {/* Partial: Single Employee Routes */}
          <Route path="employees/:employeeId" element={<ProfilPagesPartial />}>
            <Route
              index
              element={
                <ProtectedRoute>
                  <EmployeeProfilDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <EmployeeProfilDashboard />
                </ProtectedRoute>
              }
            />
            {['data/personal', 'data/personal/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <EmployeeProfilPersonalData />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
            {['data/contact', 'data/contact/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <EmployeeProfilContactData />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
            {['data/address', 'data/address/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <EmployeeProfilAddressData />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
            {['data/tax', 'data/tax/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <EmployeeProfilTaxData />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
            {['data/position', 'data/position/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <EmployeeProfilPositionData />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
            {['data/groups', 'data/groups/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <EmployeeProfilGroupData />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
            {['data/picture', 'data/picture/edit'].map((path, index) => (
              <Route
                path={path}
                element={
                  <ProtectedRoute>
                    <EmployeeProfilePictureUpload />
                  </ProtectedRoute>
                }
                key={index}
              />
            ))}
            <Route path="benefits" element={<EmployeeBenefitsPage />}>
              <Route
                path="overview"
                element={
                  <ProtectedRoute>
                    <EmployeeBenefitsOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="budget"
                element={
                  <ProtectedRoute>
                    <EmployeeBenefitBudget />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="receipts"
              element={
                <ProtectedRoute>
                  <EmployeeReceiptsOverview />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="receipts" element={<ReceiptsPagesPartial />}>
            <Route
              index
              element={
                <ProtectedRoute condition={!isBonusStandalone} redirectTo="/bonus">
                  <ReceiptsOverview />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="receipts/:receiptId" element={<ReceiptsPagesPartial />}>
            <Route
              index
              element={
                <ProtectedRoute condition={!isBonusStandalone} redirectTo="/bonus">
                  <ReiceiptDetailView />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="bonus">
            <Route
              index
              element={
                <ProtectedRoute>
                  <Bonus />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="exports" element={<ExportsPagesPartial />}>
            <Route
              index
              element={
                <ProtectedRoute>
                  <ExportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="overview"
              element={
                <ProtectedRoute>
                  <ExportsOverview />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="exports" element={<ExportFlow />}>
            <Route
              path=":type/:step"
              element={
                <ProtectedRoute>
                  <ExportStepWrapper />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="imports" element={<ExportsPagesPartial />}>
            <Route
              index
              element={
                <ProtectedRoute>
                  <ImportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path=":importId"
              element={
                <ProtectedRoute>
                  <TaskDetailsPage />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="calculator">
            <Route
              index
              element={
                <ProtectedRoute>
                  <BenefitCalculator />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="kunden-werben-kunden">
            <Route
              index
              element={
                <ProtectedRoute>
                  <KundenWerbenKunden />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="company" element={<Company />}>
            <Route index element={<Navigate replace to="settings/core-data" />} />
            <Route path="settings">
              <Route
                path="core-data"
                element={
                  <ProtectedRoute>
                    <CompanyCoreData />
                  </ProtectedRoute>
                }
              />
              <Route
                path="contact-data"
                element={
                  <ProtectedRoute>
                    <CompanyContactData />
                  </ProtectedRoute>
                }
              />
              <Route
                path="company-logo"
                element={
                  <ProtectedRoute>
                    <CompanyLogo />
                  </ProtectedRoute>
                }
              />
              <Route path="locations">
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <CompanyLocationsData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":locationId"
                  element={
                    <ProtectedRoute>
                      <LocationData />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="departments">
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <CompanyDepartmentsData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":departmentId"
                  element={
                    <ProtectedRoute>
                      <DepartmentData />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="cost-centers">
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <CompanyCostCentersData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":costCenterId"
                  element={
                    <ProtectedRoute>
                      <CostCenterData />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="groups" element={<GroupContextProvider />}>
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <GroupData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":groupId"
                  element={
                    <ProtectedRoute>
                      <GroupData />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="payroll"
                element={
                  <ProtectedRoute>
                    <CompanyPayrollSettings />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="payment">
              <Route
                path="general"
                element={
                  <ProtectedRoute>
                    <CompanyPaymentData />
                  </ProtectedRoute>
                }
              />
              <Route
                path="address"
                element={
                  <ProtectedRoute>
                    <CompanyPaymentAddressData />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="integrations">
              <Route
                path="hris-pause"
                element={
                  <ProtectedRoute>
                    <HrisPause />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="security">
              <Route
                path="app"
                element={
                  <ProtectedRoute>
                    <ManageAppSingleSignOn />
                  </ProtectedRoute>
                }
              />
              <Route
                path="platform"
                element={
                  <ProtectedRoute>
                    <ManagePlatformSingleSignOn />
                  </ProtectedRoute>
                }
              />
              <Route
                path="general"
                element={
                  <ProtectedRoute>
                    <CompanyGeneralSecurity />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="account">
              <Route
                path="password"
                element={
                  <ProtectedRoute>
                    <UserChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="twofactor"
                element={
                  <ProtectedRoute>
                    <ManageTwoFactorAuth />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>
          {/* 
        404 Route
        if the user is not Logged in and the route doesn't exist, they will be forwarded to login
      */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFoundPage />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </>
  );
};

export default observer(AppRouter);
